import React, { useMemo, useRef } from 'react';
import classNames from 'classnames';
import { Spinner } from '../Spinner/Spinner';
import { Icon } from '../Icon/Icon';
import uuidv4 from "../../utils/uuidv4";
/**
 * Bronson Upload component.
 * @see https://bronson.vwfs.io/default/components/detail/bronson-upload.html
 * @constructor
 */
export function Upload({ accept, buttonText, className, descriptionSupplementaryText, descriptionText, disabled, disabledSupplementaryText, disabledText, id, inputProps, loading, multiple, name, onChange, processingText, required, stateUpdateText, testId, ...otherProps }) {
    /**
     * Generate unique id, if prop `id` is not provided to improve accessibility.
     */
    const uploadId = useMemo(() => id ?? uuidv4('upload-'), [id]);
    const uploadElement = useRef(null);
    const uploadClassNameList = classNames('c-upload', {
        // Convenience prop from Bronson variants.
        'is-loading': loading,
    }, className).trim();
    /**
     * Render a pure presentational button.
     * @returns {React.JSX.Element | null}
     */
    function renderUploadButtonText() {
        if (buttonText) {
            return (React.createElement("span", { className: "c-upload__button c-btn", "aria-hidden": "true" },
                React.createElement("span", null, buttonText)));
        }
        return null;
    }
    /**
     * Live region to convey dynamic changes to the input, e.g. when files finished to load.
     * @returns {React.JSX.Element}
     */
    function renderLiveRegion() {
        return !disabled && stateUpdateText ? (React.createElement("div", { className: "c-input__live-region", "aria-live": "polite", id: `upload-live-region-${uploadId}` }, stateUpdateText)) : null;
    }
    /**
     * Render loading state with {@link Spinner}.
     * @returns {React.JSX.Element}
     */
    function renderLoader() {
        return (React.createElement("div", { className: "c-upload__loader", "aria-hidden": "true" },
            React.createElement(Spinner, { center: true }),
            React.createElement("span", { className: "c-upload__loader-text" }, processingText || 'Processing')));
    }
    return (React.createElement("div", { ...otherProps, "data-testid": testId, className: uploadClassNameList, ref: uploadElement },
        renderLiveRegion(),
        React.createElement("input", { className: "c-upload__input", type: "file", id: uploadId, onChange: onChange, accept: accept, multiple: multiple, name: name, required: required, "aria-describedby": `upload-description-${uploadId}`, "aria-disabled": disabled, ...inputProps }),
        React.createElement("div", { className: "c-upload__description" },
            React.createElement(Icon, { className: "c-upload__icon", viaCss: true }),
            React.createElement("label", { className: "c-upload__description-text", htmlFor: `upload-description-${uploadId}` }, disabled ? disabledText : descriptionText),
            React.createElement("span", { className: "c-upload__description-supplementary-text", id: `upload-description-${uploadId}` }, disabled ? disabledSupplementaryText : descriptionSupplementaryText)),
        renderUploadButtonText(),
        renderLoader()));
}
